/**
 * Offer Cost Breakdown Service
 * Handles database operations for offer cost breakdown data
 * Uses consolidated schema: ONE ROW PER OFFER with all contract terms in cost_data JSONB
 */

import { supabase } from '../../../foundation/altzero_base/base/supabase/supabaseClient';
import { ContractTermBreakdown, OfferCostData } from '../types/offerDetailsTypes';
import { contractWeightOf } from './calculationService';

/**
 * Database row interface matching the consolidated table structure
 */
export type OfferCostBreakdownRow = {
  id: string;
  offer_id: string;
  cost_data: Record<string, ContractTermBreakdown>;
  rate_type: string | null;
  calculated_at: string;
  created_at: string;
  updated_at: string;
};

/**
 * Save complete cost breakdown data for an offer
 * Uses consolidated schema: single UPSERT per offer
 */
export const saveOfferCostBreakdowns = async (
  offerCostData: OfferCostData
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Verify the offer exists
    const { data: existingOffer, error: checkError } = await supabase
      .from('offer')
      .select('id')
      .eq('id', offerCostData.offerId)
      .single();

    if (checkError || !existingOffer) {
      return {
        success: false,
        error: `Offer ${offerCostData.offerId} does not exist in the database.`
      };
    }

    // Build consolidated cost_data JSONB
    const costData: Record<string, Record<string, number | unknown>> = {};
    Object.entries(offerCostData.contractBreakdowns).forEach(([termMonths, breakdown]) => {
      const total = breakdown.energy + breakdown.environment + breakdown.network + breakdown.others;
      const contractYears = parseInt(termMonths) / 12;
      // perYear is the ANNUAL figure, not total/years. The Offer Details step already
      // computes it as sum(sites[*].total); recomputing it as total/contractYears here
      // divided a day-fraction-weighted total by an unweighted year count, which put the
      // annual rate out by 1/365 in the opposite direction to the contract total.
      const perYear = Number.isFinite(breakdown.perYear as number) && (breakdown.perYear as number) > 0
        ? (breakdown.perYear as number)
        : (contractYears > 0 ? total / contractYears : 0);

      costData[termMonths] = {
        energy: breakdown.energy,
        environment: breakdown.environment,
        network: breakdown.network,
        others: breakdown.others,
        daily_supply: 0,
        total,
        perYear,
        // Whole-of-term multiplier (term years − 1/365) for the ANNUAL figures in sites[].
        // The proposal renderer applies it once, and falls back to a plain term/12 when the
        // key is absent — which is exactly what this writer used to leave it to do, printing
        // every contract total one day too long (Year 1 out by ~$68 on a $25k tender, and the
        // same 1/365 on every longer term). See scopingAi.ts's `f`.
        // ⚠️ The ENGINE's weight wins whenever it supplied one. It is the only party that has
        // seen the term's quoted date range, and for a term the retailer did not sell in whole
        // years that range is the whole story: AGL's "16 Months 12 Days" weighs 1.364384, where
        // recomputing from the month count alone gives 1.330594 and under-bills the contract by
        // 2.47%. Overwriting it here made this writer disagree with the engine that produced the
        // very figures it is storing, so the Offer Details table and the generated tender showed
        // two different totals for one offer. The month-count form remains the fallback for
        // breakdowns produced before the engine published a weight.
        term_weight: contractWeightOf(breakdown, parseInt(termMonths)),
        yearlyBreakdown: [],
        // Per-site rate/line-item detail — MUST be preserved here. The document
        // generator (buildProposalData / Site Cost Analysis / rate columns in
        // List of All Offers) reads cost_data[term].sites[*].line_items directly.
        // Dropping this field silently (as this function previously did) leaves
        // those sections blank even though the aggregate totals above are correct.
        sites: breakdown.sites ?? [],
      };
    });

    // Single UPSERT - one row per offer
    const { error: upsertError } = await supabase
      .from('offer_cost_breakdown')
      .upsert({
        offer_id: offerCostData.offerId,
        cost_data: costData,
        rate_type: 'Smooth',
        calculated_at: new Date().toISOString(),
      }, { onConflict: 'offer_id' });

    if (upsertError) {
      console.error('Failed to upsert cost breakdown:', upsertError);
      return { success: false, error: upsertError.message };
    }

    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: message };
  }
};

/**
 * Get cost breakdown data for a specific offer
 * Returns single consolidated row
 */
export const getOfferCostBreakdown = async (
  offerId: string
): Promise<{ success: boolean; data?: OfferCostBreakdownRow; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from('offer_cost_breakdown')
      .select('*')
      .eq('offer_id', offerId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { success: true, data: undefined };
      }
      throw error;
    }

    return { success: true, data: data as OfferCostBreakdownRow };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: message };
  }
};

/**
 * Get cost breakdowns for multiple offers
 */
export const getMultipleOfferCostBreakdowns = async (
  offerIds: string[]
): Promise<{ success: boolean; data?: OfferCostBreakdownRow[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from('offer_cost_breakdown')
      .select('*')
      .in('offer_id', offerIds);

    if (error) throw error;

    return { success: true, data: (data || []) as OfferCostBreakdownRow[] };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: message };
  }
};

/**
 * Delete cost breakdown for a specific offer
 */
export const deleteOfferCostBreakdown = async (
  offerId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('offer_cost_breakdown')
      .delete()
      .eq('offer_id', offerId);

    if (error) throw error;

    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: message };
  }
};

/**
 * Get cost breakdown for template variables (specific offer and optional contract term)
 * Returns data in a format optimized for template usage
 */
export const getCostBreakdownForTemplate = async (
  offerId: string,
  contractTermMonths?: number
): Promise<{
  success: boolean;
  data?: Record<string, { energy: number; environment: number; network: number; others: number; total: number; perYear: number }>;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('offer_cost_breakdown')
      .select('cost_data')
      .eq('offer_id', offerId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { success: true, data: {} };
      }
      throw error;
    }

    const costData = (data?.cost_data || {}) as Record<string, { energy: number; environment: number; network: number; others: number; total: number; perYear: number }>;

    // Filter by specific term if requested
    if (contractTermMonths) {
      const termKey = contractTermMonths.toString();
      if (costData[termKey]) {
        return { success: true, data: { [termKey]: costData[termKey] } };
      }
      return { success: true, data: {} };
    }

    return { success: true, data: costData };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: message };
  }
};

/**
 * Convert consolidated database row back to frontend OfferCostData format
 */
export const convertToOfferCostData = (
  row: OfferCostBreakdownRow,
  offerName: string,
  retailerId: string
): OfferCostData | null => {
  if (!row || !row.cost_data) return null;

  const costData = row.cost_data;
  const contractTerms: number[] = [];
  const contractBreakdowns: { [termMonths: number]: ContractTermBreakdown } = {};

  Object.entries(costData).forEach(([termStr, breakdown]) => {
    const termMonths = parseInt(termStr, 10);
    if (isNaN(termMonths)) return;

    contractTerms.push(termMonths);
    contractBreakdowns[termMonths] = {
      energy: breakdown.energy || 0,
      environment: breakdown.environment || 0,
      network: breakdown.network || 0,
      others: breakdown.others || 0,
      total: breakdown.total || 0,
      perYear: breakdown.perYear || 0,
    };
  });

  return {
    offerId: row.offer_id,
    offerName,
    retailerId,
    contractTerms: contractTerms.sort((a, b) => a - b),
    contractBreakdowns,
    isComplete: contractTerms.length > 0,
    lastUpdated: new Date(row.updated_at),
  };
};
